import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import vahoy from '../../../javascript/vahoy';
import AiDetectorModal from './ai_detector_modal';
import { authenticatedHeadersHash } from '../../../javascript/v2_authentication';
import withV2HealthCheck from '../../with_v2_health_check';
import stripHtmlToVisibleText from '../../../javascript/stripHtmlToVisibleText';

const AI_DETECTOR_REQUEST_PATHS_KEY = 'ai_detector_request_paths';

const getStoredRequestPaths = () => {
  if (typeof window === 'undefined') return [];
  try {
    const storedValue = window.localStorage.getItem(AI_DETECTOR_REQUEST_PATHS_KEY);
    if (!storedValue) return [];
    return JSON.parse(storedValue);
  } catch (error) {
    window.localStorage.removeItem(AI_DETECTOR_REQUEST_PATHS_KEY);
    return [];
  }
};

const persistRequestPath = (requestPath) => {
  if (typeof window === 'undefined' || !requestPath) return;

  try {
    const storedPaths = getStoredRequestPaths();
    if (!storedPaths.includes(requestPath)) {
      window.localStorage.setItem(AI_DETECTOR_REQUEST_PATHS_KEY, JSON.stringify([...storedPaths, requestPath]));
    }
  } catch (error) {
    window.localStorage.setItem(AI_DETECTOR_REQUEST_PATHS_KEY, JSON.stringify([requestPath]));
  }
};

function AiDetectorButton({
                            text, pitchId, ahoyObjectGid, displayAsButton, checkAiDetectorVisibility
                          ,
}) {
  // AI detection consumes VISIBLE text — rich (html) pitches would send markup to the vendor.
  const plainText = stripHtmlToVisibleText(text);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiResult, setAiResult] = useState({
    predictedClass: '',
    classProbability: { ai: 0, human: 0, mixed: 0 },
  });
  const [hideAiButton, setHideAiButton] = useState(false);

  const buttonClassName = displayAsButton
    ? 'btn ai-detector-button'
    : '';

  const buttonStyle = !displayAsButton
    ? {
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
      }
    : {};

  useEffect(() => {
    if (!checkAiDetectorVisibility || typeof window === 'undefined') {
      setHideAiButton(false);
      return;
    }

    const storedPaths = getStoredRequestPaths();
    setHideAiButton(storedPaths.includes(window.location.pathname));
  }, [checkAiDetectorVisibility]);

  const handleClick = async () => {
    setShowModal(true);
    setLoading(true);
    vahoy.track('AiDetectorButton#showModal', { text: plainText, object_gid: ahoyObjectGid });

    try {
      const response = await fetch(
        `${window.V2_URL}/enrich/api/ai_text_detection?service=gptzero${typeof pitchId !== 'undefined' ? `&pitch_id=${pitchId}` : ''}`,
        {
          method: 'POST',
          headers: {
            ...authenticatedHeadersHash(),
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ text: text.slice(0, 1500) }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to analyze text');
      }

      const data = await response.json();
      const document = data.documents[0];
      const predictedClass = document.predicted_class;
      const classProbability = {
                                ai: parseFloat(document.class_probabilities.ai),
                                human: parseFloat(document.class_probabilities.human),
                                mixed: parseFloat(document.class_probabilities.mixed),
                              };

      setAiResult({
        predictedClass,
        classProbability,
      });

      if (checkAiDetectorVisibility && typeof window !== 'undefined') {
        persistRequestPath(window.location.pathname);
        setHideAiButton(true);
      }
    } catch (error) {
      setAiResult({
        predictedClass: '',
        classProbability: { ai: 0, human: 0, mixed: 0 },
      });
    } finally {
      setLoading(false); // Set loading to false after the API call finishes
    }
  };

  const handleClose = () => {
    if (!text) return;
    setShowModal(false);
    setAiResult({
      predictedClass: '',
      classProbability: { ai: 0, human: 0, mixed: 0 },
    });
    setLoading(true); // Reset loading for the next API call
  };

  return (
    <>
      {(!checkAiDetectorVisibility || !hideAiButton) && (
        <button
          className={buttonClassName}
          onClick={handleClick}
          type="button"
          disabled={displayAsButton && !text}
          style={buttonStyle}
        >
          <i className="fa-solid fa-sparkles" /> Check for AI
        </button>
      )}
      <AiDetectorModal
        text={plainText}
        showModal={showModal}
        onClose={handleClose}
        aiResult={aiResult}
        loading={loading}
        ahoyObjectGid={ahoyObjectGid}
      />
    </>
  );
}

AiDetectorButton.propTypes = {
  text: PropTypes.string,
  pitchId: PropTypes.number,
  ahoyObjectGid: PropTypes.string,
  displayAsButton: PropTypes.bool,
  checkAiDetectorVisibility: PropTypes.bool,
};

AiDetectorButton.defaultProps = {
  text: undefined,
  pitchId: undefined,
  ahoyObjectGid: undefined,
  displayAsButton: true,
  checkAiDetectorVisibility: false,
};

export default withV2HealthCheck(AiDetectorButton, null);
